import { useState } from 'react';
import { Search, X } from 'lucide-react';
import type { TeamSearchResultDto } from '../../../../domain/contracts/api.contracts';
import { useTeamSearch } from '../../../../application/hooks/team/useTeamQueries';
import { useTranslation } from '../../../../application/hooks/useTranslation';
import { Badge } from '../../../components/ui/Badge';
import { Spinner } from '../../../components/ui/Spinner';
import { initials, tierTone } from '../teamFormat';

/** E25 subtree search; picking a result jumps the navigator to the member (PRD 7.5.1 A). */
export function TeamSearch({ onPick }: { onPick: (result: TeamSearchResultDto) => void }) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const search = useTeamSearch(text);
  const active = text.trim().length > 0;

  const pick = (r: TeamSearchResultDto) => {
    setText('');
    onPick(r);
  };

  return (
    <div className="tm-search">
      <div className="tm-search-box">
        <Search size={15} className="tm-search-icon" aria-hidden />
        <input
          type="search"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t('team.searchHint')}
          aria-label={t('team.searchHint')}
          data-testid="team-search"
        />
        {active && (
          <button type="button" className="tm-search-clear" onClick={() => setText('')} aria-label="Clear search">
            <X size={14} />
          </button>
        )}
      </div>
      {active && (
        <div className="tm-search-pop" role="listbox" aria-label={t('team.searchHint')}>
          {text.trim().length < 2 && <p className="tm-search-note">{t('team.searchMin')}</p>}
          {text.trim().length >= 2 && search.isFetching && !search.data && (
            <p className="tm-search-note"><Spinner size={14} /></p>
          )}
          {search.data && search.debouncedQuery.length >= 2 && search.data.length === 0 && (
            <p className="tm-search-note">{t('team.searchEmpty')}</p>
          )}
          {search.data?.map((r) => (
            <button
              key={r.card.id}
              type="button"
              role="option"
              aria-selected={false}
              className="tm-search-row"
              onClick={() => pick(r)}
              data-testid="team-search-result"
            >
              <span className={`tm-avatar tm-avatar--sm tm-avatar--${r.card.tier.toLowerCase()}`} aria-hidden>
                {initials(r.card.fullName)}
              </span>
              <span className="tm-search-text">
                <span className="tm-search-name">
                  {r.card.fullName} <Badge tone={tierTone(r.card.tier)}>{r.card.tier}</Badge>
                </span>
                <span className="tm-search-path tabular">
                  {r.card.apartmentId} ·{' '}
                  {t('team.searchUnder', {
                    path: r.path.length <= 1 ? t('team.myTeam') : r.path.slice(1).map((p) => p.fullName).join(' › '),
                  })}
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
